import React, { useState, useEffect, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Divider,
  Menu,
  MenuItem,
  Popover,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatUnderlined as UnderlineIcon,
  FormatStrikethrough as StrikethroughIcon,
  FormatListBulleted as BulletListIcon,
  FormatListNumbered as NumberListIcon,
  FormatAlignLeft as AlignLeftIcon,
  FormatAlignCenter as AlignCenterIcon,
  FormatAlignRight as AlignRightIcon,
  FormatAlignJustify as AlignJustifyIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  FormatColorText as TextColorIcon,
  FormatQuote as FormatQuoteIcon,
  Code as CodeIcon,
  HorizontalRule as HorizontalRuleIcon,
  EmojiEmotions as EmojiEmotionsIcon,
  FormatClear as FormatClearIcon,
  Subscript as SubscriptIcon,
  Superscript as SuperscriptIcon,
  TableChart as TableIcon,
  FormatIndentIncrease as IndentIncreaseIcon,
  FormatIndentDecrease as IndentDecreaseIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardDoubleArrowDown as KeyboardDoubleArrowDownIcon,
  BorderColor as HighlightIconAlt,
} from '@mui/icons-material';
import { useEditor, EditorContent, Extension } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import LinkExtension from '@tiptap/extension-link';
import ImageExtension from '@tiptap/extension-image';
import {
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';

// Custom Image extension with width/height support
const ResizableImage = ImageExtension.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('width') || null,
        renderHTML: (attributes: any) => {
          if (!attributes.width) return {};
          return { width: attributes.width };
        },
      },
      height: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('height') || null,
        renderHTML: (attributes: any) => {
          if (!attributes.height) return {};
          return { height: attributes.height };
        },
      },
    };
  },
});
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import FontFamily from '@tiptap/extension-font-family';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Placeholder from '@tiptap/extension-placeholder';

// Custom FontSize extension for TipTap
const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return { types: ['textStyle'] };
  },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: (element: HTMLElement) => element.style.fontSize?.replace(/['"]+/g, ''),
          renderHTML: (attributes: any) => {
            if (!attributes.fontSize) return {};
            return { style: `font-size: ${attributes.fontSize}` };
          },
        },
      },
    }];
  },
});
import api from '../../utils/api';

interface EmailSignature {
  id: string;
  name: string;
  htmlContent: string;
  textContent?: string;
  forAddress?: string;
  isDefault: boolean;
  includeInReplies: boolean;
  includeInForwards: boolean;
}

// Helper: convert file to base64 data URL
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Default max width for signature images (like Zoho)
const SIGNATURE_IMG_MAX_WIDTH = 200;
const IMG_SIZES = [
  { label: 'Std', width: SIGNATURE_IMG_MAX_WIDTH, tooltip: `Standard (${SIGNATURE_IMG_MAX_WIDTH}px)` },
];

const normalizeSignatureImages = (html: string): string => {
  if (!html) return '';

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const images = doc.body.querySelectorAll('img');

    images.forEach((img) => {
      const styleAttr = img.getAttribute('style') || '';
      const widthFromAttr = parseFloat((img.getAttribute('width') || '').replace('px', ''));
      const widthMatch = styleAttr.match(/(?:^|;)\s*width\s*:\s*([0-9.]+)px/i);
      const maxWidthMatch = styleAttr.match(/(?:^|;)\s*max-width\s*:\s*([0-9.]+)px/i);
      const widthFromStyle = widthMatch ? parseFloat(widthMatch[1]) : NaN;
      const widthFromMaxStyle = maxWidthMatch ? parseFloat(maxWidthMatch[1]) : NaN;
      const preferredWidth = [widthFromAttr, widthFromStyle, widthFromMaxStyle].find(
        (n) => Number.isFinite(n) && n > 0
      );
      const finalWidth = Math.min(preferredWidth || SIGNATURE_IMG_MAX_WIDTH, SIGNATURE_IMG_MAX_WIDTH);

      img.setAttribute('width', String(finalWidth));

      img.removeAttribute('height');

      const existingStyle = img.getAttribute('style') || '';
      const styleParts = existingStyle
        .split(';')
        .map((s) => s.trim())
        .filter(Boolean)
        .filter(
          (s) =>
            !/^width\s*:/i.test(s) &&
            !/^max-width\s*:/i.test(s) &&
            !/^min-width\s*:/i.test(s) &&
            !/^height\s*:/i.test(s) &&
            !/^max-height\s*:/i.test(s) &&
            !/^min-height\s*:/i.test(s) &&
            !/^transform\s*:/i.test(s) &&
            !/^zoom\s*:/i.test(s)
        );

      styleParts.push(`width: ${finalWidth}px !important`);
      styleParts.push(`max-width: ${SIGNATURE_IMG_MAX_WIDTH}px !important`);
      styleParts.push('height: auto !important');
      styleParts.push('display: inline-block');
      img.setAttribute('style', styleParts.join('; '));
    });

    return doc.body.innerHTML;
  } catch {
    return html;
  }
};

const signatureHtmlToPreviewText = (html: string): string => {
  if (!html) return 'No content';
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const text = (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
    return text || 'No content';
  } catch {
    return html.replace(/<[^>]*>/g, '').trim() || 'No content';
  }
};

// ===== Signature Rich Text Editor Component =====
interface SignatureEditorProps {
  content: string;
  onChange: (html: string) => void;
}

const FONT_SIZES = ['8', '10', '11.5', '12', '14', '18', '24', '36', '39', '40'];
const FONT_FAMILIES = [
  'Arial',
  'Calibri',
  'Cambria',
  'Comic Sans MS',
  'Courier New',
  'Georgia',
  'Helvetica',
  'Lucida Console',
  'Roboto',
  'Segoe UI',
  'Tahoma',
  'Times New Roman',
  'Trebuchet MS',
  'Verdana',
];

const SignatureEditor: React.FC<SignatureEditorProps> = ({ content, onChange }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fontSizeMenuAnchor, setFontSizeMenuAnchor] = useState<null | HTMLElement>(null);
  const [fontFamilyMenuAnchor, setFontFamilyMenuAnchor] = useState<null | HTMLElement>(null);
  const [toolbarOverflowAnchor, setToolbarOverflowAnchor] = useState<null | HTMLElement>(null);
  const [selectedFontSize, setSelectedFontSize] = useState('11.5');
  const [selectedFontFamily, setSelectedFontFamily] = useState('Arial');
  const [customFontSize, setCustomFontSize] = useState('');
  const userPickedSizeRef = useRef(false);

  const applyFontSize = (size: string) => {
    const value = parseFloat(size);
    if (!Number.isFinite(value) || value <= 0 || value > 200) return;
    userPickedSizeRef.current = true;
    setSelectedFontSize(String(value));
    editor?.chain().focus().setMark('textStyle', { fontSize: `${value}pt` }).run();
    setFontSizeMenuAnchor(null);
  };

  const applyFontFamily = (family: string) => {
    setSelectedFontFamily(family);
    editor?.chain().focus().setFontFamily(family).run();
    setFontFamilyMenuAnchor(null);
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      LinkExtension.configure({ openOnClick: false, HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' } }),
      ResizableImage.configure({ inline: true, allowBase64: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      FontFamily,
      Highlight.configure({ multicolor: true }),
      Subscript,
      Superscript,
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      FontSize,
      Placeholder.configure({ placeholder: 'Type your signature here...' }),
    ],
    content: content || '',
    editorProps: {
      attributes: { class: 'signature-editor' },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            const file = item.getAsFile();
            if (file) {
              fileToBase64(file).then((base64) => {
                // Auto-size: load image to get dimensions, then constrain
                const img = new window.Image();
                img.onload = () => {
                  const attrs: any = {
                    src: base64,
                    width: SIGNATURE_IMG_MAX_WIDTH,
                    style: `width: ${SIGNATURE_IMG_MAX_WIDTH}px !important; max-width: ${SIGNATURE_IMG_MAX_WIDTH}px !important; height: auto !important; display: inline-block`,
                  };
                  view.dispatch(view.state.tr.replaceSelectionWith(
                    view.state.schema.nodes.image.create(attrs)
                  ));
                };
                img.src = base64;
              });
            }
            return true;
          }
        }
        return false;
      },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return false;
        for (let i = 0; i < files.length; i++) {
          if (files[i].type.startsWith('image/')) {
            event.preventDefault();
            fileToBase64(files[i]).then((base64) => {
              const img = new window.Image();
              img.onload = () => {
                const { schema } = view.state;
                const attrs: any = {
                  src: base64,
                  width: SIGNATURE_IMG_MAX_WIDTH,
                  style: `width: ${SIGNATURE_IMG_MAX_WIDTH}px !important; max-width: ${SIGNATURE_IMG_MAX_WIDTH}px !important; height: auto !important; display: inline-block`,
                };
                const node = schema.nodes.image.create(attrs);
                const pos = view.posAtCoords({ left: event.clientX, top: event.clientY });
                if (pos) {
                  view.dispatch(view.state.tr.insert(pos.pos, node));
                }
              };
              img.src = base64;
            });
            return true;
          }
        }
        return false;
      },
    },
    onUpdate: ({ editor: e }) => {
      onChange(e.getHTML());
    },
  });

  // Sync content when editing different signatures
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImageUpload = async () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be smaller than 2MB for signatures');
      return;
    }
    const base64 = await fileToBase64(file);
    // Auto-size uploaded image
    const img = new window.Image();
    img.onload = () => {
      const attrs: any = {
        src: base64,
        width: SIGNATURE_IMG_MAX_WIDTH,
        style: `width: ${SIGNATURE_IMG_MAX_WIDTH}px !important; max-width: ${SIGNATURE_IMG_MAX_WIDTH}px !important; height: auto !important; display: inline-block`,
      };
      editor.chain().focus().setImage(attrs).run();
    };
    img.src = base64;
    e.target.value = '';
  };

  const handleInsertImageUrl = () => {
    const url = window.prompt('Enter image URL:');
    if (url && editor) {
      // Load URL image to auto-size
      const img = new window.Image();
      img.onload = () => {
        const attrs: any = {
          src: url,
          width: SIGNATURE_IMG_MAX_WIDTH,
          style: `width: ${SIGNATURE_IMG_MAX_WIDTH}px !important; max-width: ${SIGNATURE_IMG_MAX_WIDTH}px !important; height: auto !important; display: inline-block`,
        };
        editor.chain().focus().setImage(attrs).run();
      };
      img.onerror = () => {
        // Insert with strict standard size even if intrinsic dimensions can't be loaded.
        editor.chain().focus().setImage({
          src: url,
          width: SIGNATURE_IMG_MAX_WIDTH,
          style: `width: ${SIGNATURE_IMG_MAX_WIDTH}px !important; max-width: ${SIGNATURE_IMG_MAX_WIDTH}px !important; height: auto !important; display: inline-block`,
        } as any).run();
      };
      img.src = url;
    }
  };

  const handleInsertLink = () => {
    if (!editor) return;
    const prevUrl = editor.getAttributes('link').href || '';
    const url = window.prompt('Enter URL:', prevUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  if (!editor) return null;

  const iconBtnSx = (isActive: boolean = false) => ({
    p: 0.5,
    borderRadius: '4px',
    bgcolor: isActive ? '#e8f0fe' : 'transparent',
    '&:hover': { bgcolor: isActive ? '#d2e3fc' : '#f1f3f4' },
  });

  return (
    <Box sx={{ border: '1px solid #dadce0', borderRadius: '4px', overflow: 'hidden' }}>
      {/* Toolbar — compact single row + overflow popover (matches ComposePage) */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.25, px: 0.75, py: 0.25,
        borderBottom: '1px solid #e0e0e0', bgcolor: '#fafafa', flexWrap: 'nowrap', minHeight: 32,
        overflow: 'hidden',
      }}>
        <Tooltip title="Undo"><span><IconButton size="small" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} sx={{ ...iconBtnSx(), p: 0.25 }}><UndoIcon sx={{ fontSize: 16, color: editor.can().undo() ? '#5f6368' : '#c4c7c5' }} /></IconButton></span></Tooltip>
        <Tooltip title="Redo"><span><IconButton size="small" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} sx={{ ...iconBtnSx(), p: 0.25 }}><RedoIcon sx={{ fontSize: 16, color: editor.can().redo() ? '#5f6368' : '#c4c7c5' }} /></IconButton></span></Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25, height: 18, alignSelf: 'center', borderColor: '#e0e0e0' }} />

        <Tooltip title="Bold"><IconButton size="small" onClick={() => editor.chain().focus().toggleBold().run()} sx={{ ...iconBtnSx(editor.isActive('bold')), p: 0.25 }}><BoldIcon sx={{ fontSize: 16, color: editor.isActive('bold') ? '#1a73e8' : '#5f6368' }} /></IconButton></Tooltip>
        <Tooltip title="Italic"><IconButton size="small" onClick={() => editor.chain().focus().toggleItalic().run()} sx={{ ...iconBtnSx(editor.isActive('italic')), p: 0.25 }}><ItalicIcon sx={{ fontSize: 16, color: editor.isActive('italic') ? '#1a73e8' : '#5f6368' }} /></IconButton></Tooltip>
        <Tooltip title="Underline"><IconButton size="small" onClick={() => editor.chain().focus().toggleUnderline().run()} sx={{ ...iconBtnSx(editor.isActive('underline')), p: 0.25 }}><UnderlineIcon sx={{ fontSize: 16, color: editor.isActive('underline') ? '#1a73e8' : '#5f6368' }} /></IconButton></Tooltip>
        <Tooltip title="Strikethrough"><IconButton size="small" onClick={() => editor.chain().focus().toggleStrike().run()} sx={{ ...iconBtnSx(editor.isActive('strike')), p: 0.25 }}><StrikethroughIcon sx={{ fontSize: 16, color: editor.isActive('strike') ? '#1a73e8' : '#5f6368' }} /></IconButton></Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25, height: 18, alignSelf: 'center', borderColor: '#e0e0e0' }} />

        {/* Font family */}
        <Tooltip title="Font">
          <Button
            size="small"
            onClick={(e) => setFontFamilyMenuAnchor(e.currentTarget)}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 14, ml: -0.5 }} />}
            sx={{
              textTransform: 'none', fontSize: '12px', color: '#3c4043', minWidth: 78, maxWidth: 78,
              px: 0.5, py: 0, height: 24, borderRadius: '4px', border: '1px solid #dadce0',
              justifyContent: 'space-between', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
              fontFamily: `"${selectedFontFamily}", sans-serif`,
              '&:hover': { bgcolor: '#f1f3f4' },
            }}
          >
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedFontFamily}</Box>
          </Button>
        </Tooltip>
        <Menu
          anchorEl={fontFamilyMenuAnchor}
          open={Boolean(fontFamilyMenuAnchor)}
          onClose={() => setFontFamilyMenuAnchor(null)}
          PaperProps={{ sx: { maxHeight: 320, minWidth: 160 } }}
        >
          {FONT_FAMILIES.map((family) => (
            <MenuItem
              key={family}
              selected={family === selectedFontFamily}
              onClick={() => applyFontFamily(family)}
              sx={{ fontSize: '13px', py: 0.5, minHeight: 'auto', fontFamily: `"${family}", sans-serif` }}
            >
              {family}
            </MenuItem>
          ))}
        </Menu>

        {/* Font size */}
        <Tooltip title="Font size">
          <Button
            size="small"
            onClick={(e) => { setCustomFontSize(selectedFontSize); setFontSizeMenuAnchor(e.currentTarget); }}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 14, ml: -0.5 }} />}
            sx={{
              textTransform: 'none', fontSize: '12px', color: '#3c4043', minWidth: 44, maxWidth: 56,
              px: 0.5, py: 0, height: 24, borderRadius: '4px', border: '1px solid #dadce0', ml: 0.25,
              '&:hover': { bgcolor: '#f1f3f4' },
            }}
          >
            {selectedFontSize}
          </Button>
        </Tooltip>
        <Menu
          anchorEl={fontSizeMenuAnchor}
          open={Boolean(fontSizeMenuAnchor)}
          onClose={() => setFontSizeMenuAnchor(null)}
          PaperProps={{ sx: { maxHeight: 320, minWidth: 90 } }}
        >
          <Box sx={{ px: 1, py: 0.5 }}>
            <TextField
              size="small"
              autoFocus
              value={customFontSize}
              placeholder="Custom"
              onChange={(e) => setCustomFontSize(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); applyFontSize(customFontSize); }
              }}
              inputProps={{ style: { fontSize: 12, padding: '4px 6px', textAlign: 'center' } }}
              sx={{ width: 70 }}
            />
          </Box>
          <Divider sx={{ my: 0.25 }} />
          {FONT_SIZES.map((size) => (
            <MenuItem
              key={size}
              selected={size === selectedFontSize}
              onClick={() => applyFontSize(size)}
              sx={{ fontSize: '13px', py: 0.5, minHeight: 'auto', justifyContent: 'center' }}
            >
              {size}
            </MenuItem>
          ))}
        </Menu>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25, height: 18, alignSelf: 'center', borderColor: '#e0e0e0' }} />

        <Tooltip title="Text color">
          <IconButton size="small" sx={{ ...iconBtnSx(), p: 0.25 }}>
            <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <TextColorIcon sx={{ fontSize: 16, color: '#5f6368' }} />
              <input type="color" style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }}
                onChange={(e) => editor.chain().focus().setColor(e.target.value).run()} />
            </label>
          </IconButton>
        </Tooltip>
        <Tooltip title="Highlight color">
          <IconButton size="small" sx={{ ...iconBtnSx(editor.isActive('highlight')), p: 0.25 }}>
            <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <HighlightIconAlt sx={{ fontSize: 16, color: editor.isActive('highlight') ? '#1a73e8' : '#5f6368' }} />
              <input type="color" style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }}
                onChange={(e) => editor.chain().focus().toggleHighlight({ color: e.target.value }).run()} />
            </label>
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25, height: 18, alignSelf: 'center', borderColor: '#e0e0e0' }} />

        <Tooltip title="Align left"><IconButton size="small" onClick={() => editor.chain().focus().setTextAlign('left').run()} sx={{ ...iconBtnSx(editor.isActive({ textAlign: 'left' })), p: 0.25 }}><AlignLeftIcon sx={{ fontSize: 16, color: '#5f6368' }} /></IconButton></Tooltip>
        <Tooltip title="Align center"><IconButton size="small" onClick={() => editor.chain().focus().setTextAlign('center').run()} sx={{ ...iconBtnSx(editor.isActive({ textAlign: 'center' })), p: 0.25 }}><AlignCenterIcon sx={{ fontSize: 16, color: '#5f6368' }} /></IconButton></Tooltip>
        <Tooltip title="Align right"><IconButton size="small" onClick={() => editor.chain().focus().setTextAlign('right').run()} sx={{ ...iconBtnSx(editor.isActive({ textAlign: 'right' })), p: 0.25 }}><AlignRightIcon sx={{ fontSize: 16, color: '#5f6368' }} /></IconButton></Tooltip>
        <Tooltip title="Justify"><IconButton size="small" onClick={() => editor.chain().focus().setTextAlign('justify').run()} sx={{ ...iconBtnSx(editor.isActive({ textAlign: 'justify' })), p: 0.25 }}><AlignJustifyIcon sx={{ fontSize: 16, color: '#5f6368' }} /></IconButton></Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.25, height: 18, alignSelf: 'center', borderColor: '#e0e0e0' }} />

        <Tooltip title="Bullet list"><IconButton size="small" onClick={() => editor.chain().focus().toggleBulletList().run()} sx={{ ...iconBtnSx(editor.isActive('bulletList')), p: 0.25 }}><BulletListIcon sx={{ fontSize: 16, color: editor.isActive('bulletList') ? '#1a73e8' : '#5f6368' }} /></IconButton></Tooltip>
        <Tooltip title="Numbered list"><IconButton size="small" onClick={() => editor.chain().focus().toggleOrderedList().run()} sx={{ ...iconBtnSx(editor.isActive('orderedList')), p: 0.25 }}><NumberListIcon sx={{ fontSize: 16, color: editor.isActive('orderedList') ? '#1a73e8' : '#5f6368' }} /></IconButton></Tooltip>

        <Tooltip title="Insert link"><IconButton size="small" onClick={handleInsertLink} sx={{ ...iconBtnSx(editor.isActive('link')), p: 0.25 }}><LinkIcon sx={{ fontSize: 16, color: editor.isActive('link') ? '#1a73e8' : '#5f6368' }} /></IconButton></Tooltip>

        <Tooltip title="More options">
          <IconButton size="small" onClick={(e) => setToolbarOverflowAnchor(e.currentTarget)} sx={{ ...iconBtnSx(Boolean(toolbarOverflowAnchor)), p: 0.25 }}>
            <KeyboardDoubleArrowDownIcon sx={{ fontSize: 16, color: toolbarOverflowAnchor ? '#1a73e8' : '#5f6368' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Overflow popover */}
      <Popover
        open={Boolean(toolbarOverflowAnchor)}
        anchorEl={toolbarOverflowAnchor}
        onClose={() => setToolbarOverflowAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { p: 0.5, display: 'flex', flexWrap: 'wrap', maxWidth: 280, gap: 0.25 } }}
      >
        <Tooltip title="Superscript"><IconButton size="small" onClick={() => editor.chain().focus().toggleSuperscript().run()} sx={iconBtnSx(editor.isActive('superscript'))}><SuperscriptIcon sx={{ fontSize: 18, color: editor.isActive('superscript') ? '#1a73e8' : '#5f6368' }} /></IconButton></Tooltip>
        <Tooltip title="Subscript"><IconButton size="small" onClick={() => editor.chain().focus().toggleSubscript().run()} sx={iconBtnSx(editor.isActive('subscript'))}><SubscriptIcon sx={{ fontSize: 18, color: editor.isActive('subscript') ? '#1a73e8' : '#5f6368' }} /></IconButton></Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.25, height: 22, alignSelf: 'center', borderColor: '#e0e0e0' }} />
        <Tooltip title="Upload image"><IconButton size="small" onClick={handleImageUpload} sx={iconBtnSx()}><ImageIcon sx={{ fontSize: 18, color: '#5f6368' }} /></IconButton></Tooltip>
        <Tooltip title="Image from URL">
          <Button size="small" onClick={handleInsertImageUrl}
            sx={{ textTransform: 'none', fontSize: '11px', color: '#5f6368', minWidth: 'auto', px: 0.75, py: 0.25, borderRadius: '4px', '&:hover': { bgcolor: '#f1f3f4' } }}>
            URL
          </Button>
        </Tooltip>
        <Tooltip title="Insert table"><IconButton size="small" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} sx={iconBtnSx()}><TableIcon sx={{ fontSize: 18, color: '#5f6368' }} /></IconButton></Tooltip>
        <Tooltip title="Quote"><IconButton size="small" onClick={() => editor.chain().focus().toggleBlockquote().run()} sx={iconBtnSx(editor.isActive('blockquote'))}><FormatQuoteIcon sx={{ fontSize: 18, color: editor.isActive('blockquote') ? '#1a73e8' : '#5f6368' }} /></IconButton></Tooltip>
        <Tooltip title="Horizontal line"><IconButton size="small" onClick={() => editor.chain().focus().setHorizontalRule().run()} sx={iconBtnSx()}><HorizontalRuleIcon sx={{ fontSize: 18, color: '#5f6368' }} /></IconButton></Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.25, height: 22, alignSelf: 'center', borderColor: '#e0e0e0' }} />
        <Tooltip title="Inline code"><IconButton size="small" onClick={() => editor.chain().focus().toggleCode().run()} sx={iconBtnSx(editor.isActive('code'))}><CodeIcon sx={{ fontSize: 18, color: editor.isActive('code') ? '#1a73e8' : '#5f6368' }} /></IconButton></Tooltip>
        <Tooltip title="Code block"><IconButton size="small" onClick={() => editor.chain().focus().toggleCodeBlock().run()} sx={iconBtnSx(editor.isActive('codeBlock'))}><CodeIcon sx={{ fontSize: 18, color: editor.isActive('codeBlock') ? '#1a73e8' : '#5f6368' }} /></IconButton></Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.25, height: 22, alignSelf: 'center', borderColor: '#e0e0e0' }} />
        <Tooltip title="Decrease indent"><IconButton size="small" onClick={() => { try { editor.chain().focus().liftListItem('listItem').run(); } catch {} }} sx={iconBtnSx()}><IndentDecreaseIcon sx={{ fontSize: 18, color: '#5f6368' }} /></IconButton></Tooltip>
        <Tooltip title="Increase indent"><IconButton size="small" onClick={() => { try { editor.chain().focus().sinkListItem('listItem').run(); } catch {} }} sx={iconBtnSx()}><IndentIncreaseIcon sx={{ fontSize: 18, color: '#5f6368' }} /></IconButton></Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.25, height: 22, alignSelf: 'center', borderColor: '#e0e0e0' }} />
        <Tooltip title="Insert emoji"><IconButton size="small" onClick={() => editor.chain().focus().insertContent('🙂').run()} sx={iconBtnSx()}><EmojiEmotionsIcon sx={{ fontSize: 18, color: '#5f6368' }} /></IconButton></Tooltip>
        <Tooltip title="Clear formatting"><IconButton size="small" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} sx={iconBtnSx()}><FormatClearIcon sx={{ fontSize: 18, color: '#5f6368' }} /></IconButton></Tooltip>
        <Box sx={{ flexBasis: '100%', height: 0 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5, py: 0.5, width: '100%' }}>
          <Typography sx={{ fontSize: 11, color: '#5f6368', mr: 0.5 }}>Image size:</Typography>
          {IMG_SIZES.map((size) => (
            <Tooltip key={size.label} title={size.tooltip}>
              <Button
                size="small"
                onClick={() => {
                  if (!editor) return;
                  const { state } = editor;
                  const { from, to } = state.selection;
                  let imageFound = false;
                  const tr = state.tr;
                  state.doc.nodesBetween(from, to, (node, pos) => {
                    if (node.type.name === 'image' && !imageFound) {
                      imageFound = true;
                      const newAttrs = { ...node.attrs, width: size.width, height: null };
                      tr.setNodeMarkup(pos, undefined, newAttrs);
                    }
                  });
                  if (!imageFound) {
                    const searchFrom = Math.max(0, from - 5);
                    const searchTo = Math.min(state.doc.content.size, from + 5);
                    state.doc.nodesBetween(searchFrom, searchTo, (node, pos) => {
                      if (node.type.name === 'image' && !imageFound) {
                        imageFound = true;
                        const newAttrs = { ...node.attrs, width: size.width, height: null };
                        tr.setNodeMarkup(pos, undefined, newAttrs);
                      }
                    });
                  }
                  if (imageFound) editor.view.dispatch(tr);
                }}
                sx={{
                  textTransform: 'none', fontSize: '10px', color: '#5f6368', minWidth: 24,
                  px: 0.5, py: 0.25, borderRadius: '4px', border: '1px solid #dadce0',
                  fontWeight: 700, lineHeight: 1,
                  '&:hover': { bgcolor: '#e8f0fe', borderColor: '#1a73e8', color: '#1a73e8' },
                }}
              >
                {size.label}
              </Button>
            </Tooltip>
          ))}
        </Box>
      </Popover>

      {/* Editor */}
      <Box sx={{
        minHeight: 180, maxHeight: 350, overflow: 'auto', px: 2, py: 1.5,
        bgcolor: '#fff',
        '& .signature-editor': {
          outline: 'none', minHeight: 160, fontSize: '11.5pt', lineHeight: 1.6,
          fontFamily: 'Arial, sans-serif', color: '#202124',
          '& p': { margin: 0, marginBottom: '4px' },
          '& a': { color: '#1a73e8', textDecoration: 'none' },
          '& img': { width: `${SIGNATURE_IMG_MAX_WIDTH}px !important`, maxWidth: `${SIGNATURE_IMG_MAX_WIDTH}px !important`, height: 'auto !important', display: 'block', margin: '4px 0', cursor: 'pointer', border: '2px solid transparent', '&:hover': { border: '2px solid #1a73e8' } },
          '& ul, & ol': { paddingLeft: '20px' },
          '& li': { marginBottom: '2px' },
          '& .is-editor-empty:first-child::before': {
            content: 'attr(data-placeholder)', color: '#9aa0a6', float: 'left', height: 0, pointerEvents: 'none',
          },
        },
      }}>
        <EditorContent editor={editor} />
      </Box>

      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileSelected} />
    </Box>
  );
};

const SignatureSettings: React.FC = () => {
  const [signatures, setSignatures] = useState<EmailSignature[]>([]);
  const [editingSignature, setEditingSignature] = useState<EmailSignature | null>(null);
  const [editName, setEditName] = useState('');
  const [editHtmlContent, setEditHtmlContent] = useState('');
  const [signaturePosition, setSignaturePosition] = useState<'above' | 'below'>('above');
  const [fromAddressNew, setFromAddressNew] = useState('');
  const [fromAddressReplies, setFromAddressReplies] = useState('');
  const [includeInReplies, setIncludeInReplies] = useState(true);
  const [includeInForwards, setIncludeInForwards] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isNewSignature, setIsNewSignature] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch signatures from API
  const fetchSignatures = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/mail/signatures');
      
      // Handle potentially double-wrapped response
      // response.data could be {success, data: [...]} OR {success, data: {success, data: [...]}}
      let sigArray: EmailSignature[] = [];
      const responseData = response.data;
      
      if (Array.isArray(responseData)) {
        sigArray = responseData;
      } else if (responseData?.data) {
        // Check if data is array or another wrapper
        if (Array.isArray(responseData.data)) {
          sigArray = responseData.data;
        } else if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          // Double wrapped: {success, data: {success, data: [...]}}
          sigArray = responseData.data.data;
        }
      }
      
      setSignatures(sigArray);
    } catch (error: any) {
      console.error('🔄 SignatureSettings: Failed to load signatures:', error);
      setSnackbar({ open: true, message: 'Failed to load signatures', severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSignatures();
  }, [fetchSignatures]);

  const handleAddSignature = () => {
    setIsNewSignature(true);
    setEditingSignature(null);
    setEditName('');
    setEditHtmlContent('');
    setSignaturePosition('above');
    setFromAddressNew('');
    setFromAddressReplies('');
    setIncludeInReplies(true);
    setIncludeInForwards(true);
    setIsDialogOpen(true);
  };

  const handleEditSignature = (sig: EmailSignature) => {
    setIsNewSignature(false);
    setEditingSignature(sig);
    setEditName(sig.name);
    // Load the HTML content directly into the rich editor
    setEditHtmlContent(normalizeSignatureImages(sig.htmlContent || ''));
    setSignaturePosition('above');
    setFromAddressNew(sig.forAddress || '');
    setFromAddressReplies('');
    setIncludeInReplies(sig.includeInReplies);
    setIncludeInForwards(sig.includeInForwards);
    setIsDialogOpen(true);
  };

  const handleSaveSignature = async () => {
    if (!editName.trim()) {
      setSnackbar({ open: true, message: 'Please enter a signature name', severity: 'error' });
      return;
    }

    setIsSaving(true);
    const wasNewSignature = isNewSignature; // Capture value before any state changes
    try {
      // The HTML content comes directly from the TipTap editor
      const htmlContent = normalizeSignatureImages(editHtmlContent);
      
      if (isNewSignature) {
        // Create new signature
        await api.post('/mail/signatures', {
          name: editName,
          htmlContent: htmlContent,
          forAddress: (fromAddressNew || fromAddressReplies || '').trim() || undefined,
          isDefault: signatures.length === 0,
          includeInReplies,
          includeInForwards,
        });
      } else if (editingSignature) {
        // Update existing signature
        await api.put(`/mail/signatures/${editingSignature.id}`, {
          name: editName,
          htmlContent: htmlContent,
          forAddress: (fromAddressNew || fromAddressReplies || '').trim() || undefined,
          includeInReplies,
          includeInForwards,
        });
      }
      
      // Fetch fresh data from server BEFORE closing dialog
      const refreshResponse = await api.get('/mail/signatures');
      
      // Handle potentially double-wrapped response
      let sigArray: EmailSignature[] = [];
      const responseData = refreshResponse.data;
      
      if (Array.isArray(responseData)) {
        sigArray = responseData;
      } else if (responseData?.data) {
        if (Array.isArray(responseData.data)) {
          sigArray = responseData.data;
        } else if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          sigArray = responseData.data.data;
        }
      }
      // Update signatures state with fresh data
      setSignatures(sigArray);
      
      // Close dialog after data is fetched
      setIsDialogOpen(false);
      setEditingSignature(null);
      setEditName('');
      setEditHtmlContent('');
      setFromAddressNew('');
      setFromAddressReplies('');
      
      setSnackbar({ open: true, message: wasNewSignature ? 'Signature created successfully' : 'Signature saved successfully', severity: 'success' });
      setIsSaving(false);
    } catch (error: any) {
      console.error('Failed to save signature:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to save signature', 
        severity: 'error' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSignature = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this signature?')) {
      return;
    }
    
    setIsSaving(true);
    try {
      await api.delete(`/mail/signatures/${id}`);
      await fetchSignatures();
      setSnackbar({ open: true, message: 'Signature deleted', severity: 'success' });
    } catch (error: any) {
      console.error('Failed to delete signature:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to delete signature', 
        severity: 'error' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSetDefault = async (id: string) => {
    setIsSaving(true);
    try {
      // Update the signature to be default
      await api.put(`/mail/signatures/${id}`, { isDefault: true });
      await fetchSignatures();
      setSnackbar({ open: true, message: 'Default signature updated', severity: 'success' });
    } catch (error: any) {
      console.error('Failed to set default:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to update default', 
        severity: 'error' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingSignature(null);
    setEditName('');
    setEditHtmlContent('');
    setFromAddressNew('');
    setFromAddressReplies('');
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.primary', mb: 3 }}>
        Email Signature
      </Typography>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Signatures list */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                My Signatures ({signatures.length})
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddSignature}
                size="small"
                disabled={isSaving}
              >
                Add Signature
              </Button>
            </Box>

            {signatures.length === 0 ? (
              <Paper
                elevation={0}
                sx={{ p: 3, bgcolor: 'action.hover', borderRadius: 1, textAlign: 'center' }}
              >
                <Typography variant="body2" color="text.secondary">
                  No signatures created. Click "Add Signature" to create one.
                </Typography>
              </Paper>
            ) : (
              <List sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                {signatures.map((sig, index) => (
                  <ListItem
                    key={sig.id}
                    sx={{
                      borderBottom: index < signatures.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {sig.name}
                          </Typography>
                          {sig.isDefault && (
                            <Chip label="Default" size="small" color="primary" sx={{ height: 20 }} />
                          )}
                          {sig.includeInReplies && (
                            <Chip label="Replies" size="small" variant="outlined" sx={{ height: 20 }} />
                          )}
                          {sig.includeInForwards && (
                            <Chip label="Forwards" size="small" variant="outlined" sx={{ height: 20 }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5, maxWidth: '500px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {(() => {
                            const previewText = signatureHtmlToPreviewText(sig.htmlContent || '');
                            return `${previewText.substring(0, 100)}${previewText.length > 100 ? '...' : ''}`;
                          })()}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      {!sig.isDefault && (
                        <Button
                          size="small"
                          onClick={() => handleSetDefault(sig.id)}
                          disabled={isSaving}
                          sx={{ mr: 1 }}
                        >
                          Set Default
                        </Button>
                      )}
                      <IconButton
                        edge="end"
                        onClick={() => handleEditSignature(sig)}
                        sx={{ mr: 0.5 }}
                        disabled={isSaving}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteSignature(sig.id)}
                        disabled={isSaving}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </>
      )}

      {/* Edit/Create Signature Dialog */}
      <Dialog 
        open={isDialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {isNewSignature ? 'Add New Signature' : 'Edit Signature'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                Signature Name *
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Enter signature name"
                autoFocus
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                Signature Content
              </Typography>
              <SignatureEditor
                content={editHtmlContent}
                onChange={(html) => setEditHtmlContent(html)}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                You can paste or upload images (logos), format text, insert links, and more using the toolbar above.
              </Typography>
            </Box>

            {/* Preview */}
            {editHtmlContent && editHtmlContent !== '<p></p>' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                  Preview
                </Typography>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    bgcolor: 'background.default',
                    '& p': { margin: 0 },
                    '& img': { maxWidth: `${SIGNATURE_IMG_MAX_WIDTH}px`, height: 'auto' },
                  }}
                >
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(normalizeSignatureImages(editHtmlContent)) }} />
                </Paper>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeInReplies}
                    onChange={(e) => setIncludeInReplies(e.target.checked)}
                  />
                }
                label="Include in replies"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={includeInForwards}
                    onChange={(e) => setIncludeInForwards(e.target.checked)}
                  />
                }
                label="Include in forwards"
              />
            </Box>

            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Signature position
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={signaturePosition === 'above'}
                      onChange={(e) => setSignaturePosition(e.target.checked ? 'above' : 'below')}
                    />
                  }
                  label="Place signature above quoted content"
                />
                <Typography variant="caption" sx={{ color: 'text.secondary', ml: 4 }}>
                  Turn off to place signature below quoted content.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Associated From addresses for new emails
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={fromAddressNew}
                onChange={(e) => setFromAddressNew(e.target.value)}
                placeholder="Enter email address"
                helperText="Optional: use this signature for a specific From address"
              />
            </Box>

            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Associated From addresses for replies and forwards
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={fromAddressReplies}
                onChange={(e) => setFromAddressReplies(e.target.value)}
                placeholder="Enter email address"
                helperText="Optional fallback for reply/forward compose"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isSaving}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveSignature}
            disabled={isSaving || !editName.trim()}
            startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
          >
            {isNewSignature ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SignatureSettings;
